import React, { useState, useCallback, useEffect, useRef } from "react";
import { Button, Spinner } from "reactstrap";
import { useForm, Controller } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { CheckCircle, Lock, Eye, EyeOff, AlertCircle, Mail, Ban } from "lucide-react";
import { useTranslation } from "react-i18next";
import BeartrackLogo from "../../assets/beartrack/beartrack_logo.png";
import { verifyTeamInvite, acceptTeamInvite } from "../../api/team";
import { Login } from "../../api/auth";
import { showToast } from "../../utils/showToast";
import { persistSessionAndRedirect } from "../../utils/sessionRedirect";
import LoginAside from "./LoginAside";

export default function AcceptInvite() {
  const { t } = useTranslation();
  const { control, handleSubmit, formState: { errors } } = useForm();
  const navigate = useNavigate();
  const { token } = useParams();

  const [verifying, setVerifying] = useState(true);
  const [invite, setInvite] = useState(null); // { email, name, scope }
  const [invalid, setInvalid] = useState(false);
  const [verifyError, setVerifyError] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  // Cuenta suspendida por falta de pago: el aviso va en pantalla (ver rama
  // `paymentBlocked`). El copy es traducible, así que no se usa el mensaje del
  // backend (viene fijo en español).
  const [paymentBlocked, setPaymentBlocked] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const submittingRef = useRef(false);

  // Verifica el token para mostrar el correo del invitado o un estado adecuado.
  // Distingue un token afirmado como inválido por el backend (HTTP 200
  // {valid:false} → terminal) de un fallo de transporte (data null → reintentable).
  const runVerify = useCallback(async () => {
    setVerifying(true);
    setInvalid(false);
    setVerifyError(false);
    const { data } = await verifyTeamInvite(token);
    if (data?.valid) {
      // `scope` distingue la invitación de un comercio de la del equipo interno
      // de una empresa logística; si el backend no lo manda (versión previa),
      // cae al copy de comercio, que era el único flujo que existía.
      setInvite({ email: data.email, name: data.name, scope: data.scope });
    } else if (data && data.valid === false) {
      setInvalid(true);
    } else {
      setVerifyError(true);
    }
    setVerifying(false);
  }, [token]);

  useEffect(() => {
    runVerify();
  }, [runVerify]);

  const submitValues = useCallback(
    async ({ password, confirmPassword }) => {
      if (password.length < 6) {
        showToast(t("La contraseña debe tener al menos 6 caracteres."), false);
        return;
      }
      if (password !== confirmPassword) {
        showToast(t("Las contraseñas no coinciden."), false);
        return;
      }
      // Guard de reentrada síncrono: cubre el doble-click que `disabled={loading}`
      // no bloquea a tiempo (el re-render que deshabilita el botón es asíncrono).
      if (submittingRef.current) return;
      submittingRef.current = true;

      setLoading(true);
      try {
        const { data, error } = await acceptTeamInvite({ password, token });
        if (error || !data?.email) {
          showToast(error?.message || t("No se pudo aceptar la invitación."), false);
          setLoading(false);
          return;
        }

        // Cuenta activada: iniciamos sesión con las credenciales recién fijadas
        // (la emisión de sesión vive en el login normal, no en ms-users).
        const login = await Login({ email: data.email, password });

        // Suspendida por falta de pago: la cuenta quedó activada, pero el
        // backend no emite sesión. auth.js no muestra toast para este caso, así
        // que el aviso va en pantalla (ver rama `paymentBlocked`) y no como éxito.
        if (login?.error?.code === "PAYMENT_BLOCKED") {
          setPaymentBlocked(true);
          return;
        }

        // Cualquier otro fallo de login: la cuenta quedó activada, así que no va el
        // verde de "ingresando", sino el error (auth.js ya no notifica: lo hace la vista).
        if (login?.error) {
          showToast(login.error.message || t("No se pudo iniciar sesión."), false);
          setSuccess(true);
          setTimeout(() => navigate("/login"), 2000);
          return;
        }

        if (login?.success && login?.data?.accessToken) {
          showToast(t("¡Cuenta activada! Ingresando…"), true);
          const result = persistSessionAndRedirect(login.data);
          if (!result.ok) {
            setSuccess(true);
            showToast(t("Tu cuenta se activó. Inicia sesión para continuar."), true);
            setTimeout(() => navigate("/login"), 2000);
          }
          return;
        }

        // Activada pero el login no devolvió sesión → a login manual.
        setSuccess(true);
        showToast(t("Tu cuenta se activó. Inicia sesión para continuar."), true);
        setTimeout(() => navigate("/login"), 2000);
      } catch (e) {
        showToast(t("Ocurrió un error, intenta nuevamente."), false);
      } finally {
        setLoading(false);
        submittingRef.current = false;
      }
    },
    [navigate, token, t],
  );

  return (
    <div className="login-split">
      <LoginAside />
      <main className="login-main">
        <div className="login-main__inner">
          <div className="login-logo-container login-logo-container--mobile">
            <img src={BeartrackLogo} alt="BearTrack" className="login-logo" />
          </div>

          {verifying ? (
            <div className="login-success">
              <Spinner />
              <p className="login-subtitle" style={{ margin: 0 }}>
                {t("Validando tu invitación…")}
              </p>
            </div>
          ) : invalid ? (
            <>
              <div className="login-header">
                <h1 className="login-title">{t("Invitación no válida")}</h1>
                <p className="login-subtitle">
                  {/* Sin token válido no sabemos de qué flujo venía, así que el
                      texto no puede asumir "tu comercio". */}
                  {t("Este enlace es inválido o ya expiró. Pídele a quien te invitó que te reenvíe la invitación.")}
                </p>
              </div>
              <div className="login-actions">
                <div className="login-forgot">
                  <a href="/login" className="login-forgot-link">
                    {t("Volver al inicio de sesión")}
                  </a>
                </div>
              </div>
            </>
          ) : verifyError ? (
            <>
              <div className="login-header">
                <h1 className="login-title">{t("No pudimos validar tu invitación")}</h1>
                <p className="login-subtitle">
                  {t("Hubo un problema de conexión. Intenta nuevamente.")}
                </p>
              </div>
              <div className="login-actions">
                <Button className="login-button" onClick={runVerify}>
                  {t("Reintentar")}
                </Button>
                <div className="login-forgot">
                  <a href="/login" className="login-forgot-link">
                    {t("Volver al inicio de sesión")}
                  </a>
                </div>
              </div>
            </>
          ) : paymentBlocked ? (
            <div role="alert">
              <div className="login-header">
                <span className="flex items-center justify-center w-14 h-14 mb-3 rounded-full bg-status-error-soft text-status-error">
                  <Ban size={28} aria-hidden="true" />
                </span>
                <h1 className="login-title">{t("Cuenta Suspendida")}</h1>
                <p className="login-subtitle">
                  {t("Tu cuenta se encuentra")}{" "}
                  <strong>{t("inactiva por falta de pago")}</strong>.
                  {t(" Por favor contacta a administración para regularizar tu situación.")}
                </p>
                {/* La activación sí ocurrió: sin esta línea el aviso se lee
                    como si la invitación hubiera fallado. */}
                <p className="login-subtitle">
                  {t("Tu cuenta quedó activada, pero no podrás ingresar hasta regularizar el pago.")}
                </p>
                <p className="login-subtitle">
                  {t("Si crees que esto es un error, escribe a")}{" "}
                  <a
                    href="mailto:emorales@grupogamu.cl"
                    className="font-medium text-brand-500 hover:text-brand-600"
                  >
                    emorales@grupogamu.cl
                  </a>
                </p>
              </div>
              <div className="login-actions">
                <div className="login-forgot">
                  <a href="/login" className="login-forgot-link">
                    {t("Volver al inicio de sesión")}
                  </a>
                </div>
              </div>
            </div>
          ) : success ? (
            <div className="login-success">
              <span className="login-success-icon">
                <CheckCircle size={28} />
              </span>
              <p className="login-success-text">{t("¡Cuenta activada!")}</p>
              <p className="login-subtitle" style={{ margin: 0 }}>
                {t("Redirigiendo al inicio de sesión…")}
              </p>
            </div>
          ) : (
            <>
              <div className="login-header">
                <h1 className="login-title">{t("Activa tu cuenta")}</h1>
                <p className="login-subtitle">
                  {invite?.scope === "logistic"
                    ? t("Crea una contraseña para acceder al panel de tu empresa.")
                    : t("Crea una contraseña para acceder al portal de tu comercio.")}
                </p>
              </div>

              {invite?.email && (
                <div className="login-field" style={{ marginBottom: 12 }}>
                  <div className="login-input-wrap" style={{ opacity: 0.85 }}>
                    <span className="login-input-icon">
                      <Mail size={18} strokeWidth={2} />
                    </span>
                    <input className="login-input" value={invite.email} readOnly disabled />
                  </div>
                </div>
              )}

              <form onSubmit={handleSubmit(submitValues)} className="login-form" noValidate>
                <div className="login-form-group">
                  <div className="login-field">
                    <label htmlFor="new-password" className="login-label">
                      {t("Contraseña")}
                    </label>
                    <div className="login-input-wrap">
                      <span className="login-input-icon">
                        <Lock size={18} strokeWidth={2} />
                      </span>
                      <Controller
                        name="password"
                        control={control}
                        defaultValue=""
                        rules={{ required: t("Ingresa una contraseña válida.") }}
                        render={({ field }) => (
                          <input
                            {...field}
                            id="new-password"
                            type={showPassword ? "text" : "password"}
                            autoComplete="new-password"
                            className="login-input"
                            placeholder="••••••••"
                          />
                        )}
                      />
                      <button
                        type="button"
                        className="login-toggle"
                        onClick={() => setShowPassword((v) => !v)}
                        aria-label={showPassword ? t("Ocultar contraseña") : t("Mostrar contraseña")}
                        tabIndex={-1}
                      >
                        {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                      </button>
                    </div>
                    {errors.password && (
                      <div className="login-error" role="alert">
                        <AlertCircle size={14} />
                        {errors.password.message}
                      </div>
                    )}
                  </div>

                  <div className="login-field">
                    <label htmlFor="confirm-password" className="login-label">
                      {t("Confirmar contraseña")}
                    </label>
                    <div className="login-input-wrap">
                      <span className="login-input-icon">
                        <Lock size={18} strokeWidth={2} />
                      </span>
                      <Controller
                        name="confirmPassword"
                        control={control}
                        defaultValue=""
                        rules={{ required: t("Repite la contraseña.") }}
                        render={({ field }) => (
                          <input
                            {...field}
                            id="confirm-password"
                            type={showPassword ? "text" : "password"}
                            autoComplete="new-password"
                            className="login-input"
                            placeholder="••••••••"
                          />
                        )}
                      />
                    </div>
                    {errors.confirmPassword && (
                      <div className="login-error" role="alert">
                        <AlertCircle size={14} />
                        {errors.confirmPassword.message}
                      </div>
                    )}
                  </div>
                </div>

                <div className="login-actions">
                  <Button
                    type="submit"
                    className={`login-button ${loading ? "loading" : ""}`}
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <Spinner size="sm" className="me-2" />
                        {t("Activando…")}
                      </>
                    ) : (
                      t("Activar cuenta")
                    )}
                  </Button>

                  <div className="login-forgot">
                    <a href="/login" className="login-forgot-link">
                      {t("Volver al inicio de sesión")}
                    </a>
                  </div>
                </div>
              </form>
            </>
          )}
        </div>
      </main>
    </div>
  );
}
